import React, { useEffect, useState, useId, memo } from 'react'
import { getWaveValuesFromAudioBuffer } from '@/utils/get-wave-values-from-audio-buffer.ts'
import { getAudioBufferFromAudioUrl } from '@/utils/get-audio-buffer-from-audio-url.ts'
import useWaveStore from '../../shared/store/wave.store.tsx'
import { useShallow } from 'zustand/react/shallow'
import { WaveValuesPlaceholder } from '@/utils/wave-values-placeholder.ts'

interface InteractiveAudioWaveProps {
  width: number
  height: number
  audioFileUrl?: string
  progress: number
  setProgress: (progress: number) => void
  isLoading?: boolean
  audioBuffer?: AudioBuffer
  color?: string
  spacing?: number
  lineWidth?: number
  isInteractive?: boolean
}

const InteractiveAudioWave: React.FC<InteractiveAudioWaveProps> = ({
  width,
  height,
  progress,
  audioFileUrl,
  setProgress,
  isLoading,
  audioBuffer,
  color = '#FFFFFF77',
  spacing = 2,
  lineWidth = 2,
  isInteractive = true,
}) => {
  const { getWave, storeWave } = useWaveStore(
    useShallow((state) => ({
      getWave: state.getWave,
      storeWave: state.storeWave,
    }))
  )
  const [audioSamples, setAudioSamples] = useState<number[]>([])
  const [hoverPos, setHoverPos] = useState<number | undefined>()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const id = useId()
  const sampleWidth = lineWidth + spacing

  useEffect(() => {
    if (!audioFileUrl) return

    const loadAudio = async () => {
      const waveValues = getWave(audioFileUrl)
      if (waveValues) {
        setAudioSamples(waveValues)
        return
      }
      setIsFetching(true)

      const buffer = await getAudioBufferFromAudioUrl(audioFileUrl)
      const samples = getWaveValuesFromAudioBuffer(buffer, width / sampleWidth)
      setAudioSamples(samples)
      setIsFetching(false)
      storeWave(audioFileUrl, samples)
    }

    void loadAudio()
  }, [audioFileUrl])

  useEffect(() => {
    if (audioBuffer) {
      setAudioSamples(
        getWaveValuesFromAudioBuffer(audioBuffer, width / sampleWidth)
      )
    }
  }, [audioBuffer, width])

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const x = event.clientX - rect.left
    setHoverPos(x / width)
  }

  const handleMouseLeave = () => {
    setHoverPos(undefined)
  }

  const handleClick = () => {
    if (hoverPos === undefined || !isInteractive) return
    setProgress(hoverPos)
  }

  const renderLoadingWave = (samples?: number[]): JSX.Element => {
    const wave = samples ?? WaveValuesPlaceholder

    return (
      <svg width={width} height={height}>
        <defs>
          <linearGradient
            id={`wave-shimmer-${id}`}
            x1="0%"
            x2="100%"
            y1="0"
            y2="0"
          >
            <stop offset="0%" stopColor="#ffffff10">
              <animate
                attributeName="offset"
                from="-1"
                to="1"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="50%" stopColor="#ffffff70">
              <animate
                attributeName="offset"
                from="-0.5"
                to="1.5"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="100%" stopColor="#ffffff10">
              <animate
                attributeName="offset"
                from="0"
                to="2"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </stop>
          </linearGradient>

          <mask id={`wave-mask-${id}`}>
            <rect
              width={width}
              height={height}
              fill={`url(#wave-shimmer-${id})`}
            />
          </mask>
        </defs>

        <g mask={`url(#wave-mask-${id})`}>
          {wave.map((avgVolume, index) => {
            const lineHeight = avgVolume * 100
            const x = index * sampleWidth + 1
            const y = (height - lineHeight) / 2

            return (
              <line
                key={index}
                x1={x}
                x2={x}
                y1={y}
                y2={y + lineHeight}
                stroke="white"
                strokeWidth={2}
                strokeLinecap="round"
              />
            )
          })}
        </g>
      </svg>
    )
  }

  const renderAudioWave = (): JSX.Element => {
    const progressX =
      width * (hoverPos !== undefined ? Math.min(hoverPos, progress) : progress)
    const hoverX =
      width * (hoverPos !== undefined ? Math.max(hoverPos, progress) : progress)

    const layers = isInteractive ? ['white', '#FFFFFFAA', color] : [color]

    return (
      <svg width={width} height={height}>
        <defs>
          {isInteractive && (
            <clipPath id={`clip-progress-${id}`}>
              <rect x="0" y="0" width={progressX} height={height} />
            </clipPath>
          )}
          <clipPath id={`clip-hover-${id}`}>
            <rect
              x={progressX}
              y="0"
              width={hoverX - progressX}
              height={height}
            />
          </clipPath>
          {isInteractive && (
            <clipPath id={`clip-remaining-${id}`}>
              <rect x={hoverX} y="0" width={width - hoverX} height={height} />
            </clipPath>
          )}
        </defs>

        {layers.map((color, i) => {
          const clipId =
            i === 0
              ? `clip-progress-${id}`
              : i === 1
                ? `clip-hover-${id}`
                : `clip-remaining-${id}`

          return (
            <g key={color} clipPath={`url(#${clipId})`}>
              {audioSamples.map((volume, index) => {
                const lineHeight = volume * height
                const x = index * sampleWidth + 1
                const y = (height - lineHeight) / 2

                return (
                  <line
                    key={`${color}-${index}`}
                    x1={x}
                    x2={x}
                    y1={y}
                    y2={y + lineHeight}
                    stroke={color}
                    strokeWidth={lineWidth}
                    strokeLinecap={isInteractive ? 'round' : 'square'}
                  />
                )
              })}
            </g>
          )
        })}
      </svg>
    )
  }

  return (
    <div
      className="w-full"
      style={{ height: height, width: width, overflow: 'hidden' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {isFetching
        ? renderLoadingWave()
        : isLoading
          ? renderLoadingWave(audioSamples)
          : audioSamples.length > 0
            ? renderAudioWave()
            : null}
    </div>
  )
}

export default memo(InteractiveAudioWave)
